// ---------------------------------------------------------------------------
// Display formatting.
//
// Money arrives from the API as a string so that no precision is lost. It is
// turned into a Decimal here, never into a plain JavaScript number, and the
// result is a string ready to render.
//
// Times arrive as UTC ISO strings and are always shown in Asia/Beirut.
// ---------------------------------------------------------------------------

import Decimal from 'decimal.js';

export const SHOP_TIMEZONE = 'Asia/Beirut';

Decimal.set({ rounding: Decimal.ROUND_HALF_UP, precision: 28 });

/// Anything that can stand for an amount. Decimal is included so the output of
/// one calculation can be fed straight into a formatter without a round trip
/// through a string.
export type Amount = string | number | Decimal | null | undefined;

export function toDecimal(value: Amount): Decimal {
  if (value === null || value === undefined || value === '') return new Decimal(0);
  try {
    const decimal = new Decimal(value as Decimal.Value);
    return decimal.isFinite() ? decimal : new Decimal(0);
  } catch {
    return new Decimal(0);
  }
}

/// 1600000 reads 1,600,000.
///
/// Exported because a box somebody types a lira price into groups the same
/// way the figures beside it do. Two pieces of arithmetic that happen to
/// agree today is how a form and its own summary drift apart.
export function groupThousands(value: string): string {
  const [whole = '0', fraction] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction ? `${grouped}.${fraction}` : grouped;
}

/// 12.00$ - the figure first, the dollar sign after it with no space, in
/// either language; a negative keeps its minus in front: -12.00$.
export function formatUsd(value: Amount): string {
  const amount = toDecimal(value).toDecimalPlaces(2);
  const sign = amount.isNegative() ? '-' : '';
  return `${sign}${groupThousands(amount.abs().toFixed(2))}$`;
}

/// What a Lebanese pound amount is called, in each language.
///
/// `LBP` in both languages - the shop's choice: one Latin token that never
/// mirrors or loses a dot beside Latin digits, whichever way the page runs;
/// the prose elsewhere in the app still says `ليرة` in full, which is right
/// in a sentence and too long on a product tile.
/// Exported so a field that takes a lira amount can label itself with the
/// same word the formatted figures use, rather than a second copy that could
/// drift.
export const LBP_LABEL = { en: 'LBP', ar: 'LBP' } as const;

/// 1,790,000 LBP
///
/// Rounded to whole pounds. There is no such thing as a fraction of a lira in
/// circulation, and a card reading `1,074,000.00` would be four wasted
/// characters on the narrowest tile in the shop.
export function formatLbp(value: Amount, locale: 'en' | 'ar' = 'en'): string {
  const amount = toDecimal(value).toDecimalPlaces(0);
  const sign = amount.isNegative() ? '-' : '';
  return `${sign}${groupThousands(amount.abs().toFixed(0))} ${LBP_LABEL[locale]}`;
}

/// Convert and format in one step, using an explicit rate.
///
/// THE ONLY conversion in the application. The product tile, the cart total,
/// the payment dialog and the shift count all come through here, so the pounds
/// a cashier reads off a card are the same pounds the customer is asked for at
/// the till - by construction, not by two pieces of arithmetic happening to
/// agree.
export function usdToLbpDisplay(usd: Amount, rate: Amount, locale: 'en' | 'ar' = 'en'): string {
  return formatLbp(toDecimal(usd).times(toDecimal(rate)), locale);
}

// ---------------------------------------------------------------------------
// There is no longer a conversion boundary for a product price.
//
// There used to be two functions here that turned a typed lira figure into
// dollars on the way into the database and back again on the way out, because
// the price column held dollars. They are gone, and so is the bug they caused:
// a dollar column four decimal places wide cannot hold a lira figure, so
// 300,000 came back out of it as 300,004 and 500,000 as 500,001.
//
// Prices are now STORED in lira, exactly, and a dollar figure is derived from
// them where dollars are wanted. `usdToLbpDisplay` above is the only
// conversion left in this file, and it converts one way: an old dollar amount
// - a legacy price, a historical sale - into the lira to show for it. Nothing
// converts back.
// ---------------------------------------------------------------------------

/// Whether two money strings mean the same amount.
///
/// `17.88` and `17.8800` are the same price written twice - the database
/// stores four places and the wire drops the trailing zeros - and comparing
/// them as TEXT is how a box that re-derives its own contents ends up
/// re-deriving them for ever. Blank is only ever equal to blank: it means
/// "not recorded", which is not the same fact as zero.
export function sameAmount(a: string, b: string): boolean {
  if (a === '' || b === '') return a === b;
  return toDecimal(a).equals(toDecimal(b));
}

/// THE weight, everywhere: trailing zeros off, every meaningful gram kept.
///
///   1.000 -> "1 kg"      1.200 -> "1.2 kg"     1.250 -> "1.25 kg"
///   2.050 -> "2.05 kg"   0.200 -> "0.2 kg"     0.125 -> "0.125 kg"
///
/// ONE formatter, deliberately. There used to be two: this one padded to
/// three decimals "so the paper proves nothing was hidden", and a Compact
/// twin trimmed for the till. The real printed receipt settled the argument
/// - «1.000 kg» on paper is noise, not proof - so the trimming rule is now
/// the rule, and the padded variant is gone rather than left around for the
/// next screen to pick up by accident.
///
/// Trimming only. The stored quantity, the sale and the stock movement keep
/// their full three-decimal precision - this is what the paper and the
/// screen SAY, never what the shop recorded.
export function formatWeight(value: Amount, unit = 'kg'): string {
  const trimmed = toDecimal(value)
    .toDecimalPlaces(3)
    // `toFixed()` with no argument keeps the significant digits and drops
    // the rest, so 1.250 becomes "1.25" and 1.257 stays "1.257".
    .toFixed();
  return `${trimmed} ${unit}`;
}

/// Quantities of anything: 4, not 4.000 - and 1.2, not 1.200.
///
/// The same trimming rule as the weights, because it IS mostly weights that
/// come through here (the preparation ticket, order history, stock rows).
/// The old version trimmed integers only, which is why kitchen paper read
/// «1.200 كغ» while the receipt beside it had just been fixed to say 1.2.
export function formatQuantity(value: Amount): string {
  return toDecimal(value).toDecimalPlaces(3).toFixed();
}

const UNIT_LABELS: Record<string, { en: string; ar: string }> = {
  KG: { en: 'kg', ar: 'كغ' },
  GRAM: { en: 'g', ar: 'غ' },
  PIECE: { en: 'piece', ar: 'قطعة' },
  TRAY: { en: 'tray', ar: 'صينية' },
  PACKAGE: { en: 'pack', ar: 'عبوة' },
  BOX: { en: 'box', ar: 'صندوق' },
  MEAL: { en: 'meal', ar: 'وجبة' },
  CUSTOM_AMOUNT: { en: 'amount', ar: 'مبلغ' },
};

export function unitLabel(unit: string, locale: 'en' | 'ar'): string {
  return UNIT_LABELS[unit]?.[locale] ?? unit.toLowerCase();
}

// --- dates and times -------------------------------------------------------

const localeTag = (locale: 'en' | 'ar') => (locale === 'ar' ? 'ar-LB' : 'en-GB');

/// 28 Jul 2026, 14:32
export function formatDateTime(iso: string | null | undefined, locale: 'en' | 'ar' = 'en'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(localeTag(locale), {
    timeZone: SHOP_TIMEZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/// 28 Jul 2026 — for a date with no meaningful time of day, such as an expiry
/// date or an invoice due date. Still read in Beirut time, so a delivery
/// recorded late at night does not show yesterday's date.
export function formatDate(iso: string | null | undefined, locale: 'en' | 'ar' = 'en'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(localeTag(locale), {
    timeZone: SHOP_TIMEZONE,
    dateStyle: 'medium',
  }).format(date);
}

/// 14:32
export function formatTime(date: Date, locale: 'en' | 'ar' = 'en'): string {
  return new Intl.DateTimeFormat(localeTag(locale), {
    timeZone: SHOP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/// Monday, 28 July 2026
export function formatLongDate(date: Date, locale: 'en' | 'ar' = 'en'): string {
  return new Intl.DateTimeFormat(localeTag(locale), {
    timeZone: SHOP_TIMEZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/// "5 minutes ago" — used in the activity feed.
export function formatRelative(iso: string, locale: 'en' | 'ar' = 'en'): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const seconds = Math.round((then - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(localeTag(locale), { numeric: 'auto' });

  const thresholds: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
  ];

  let value = seconds;
  for (const [unit, limit] of thresholds) {
    if (Math.abs(value) < limit) return formatter.format(Math.round(value), unit);
    value /= limit;
  }
  return formatter.format(Math.round(value), 'year');
}
